// Defines the days, weeks, and months that reports can cover.
// The calendar uses these options to offer selectable dates. Graphs use the same
// options for bar boundaries and labels, keeping the selected dates and reported data aligned.
import { DateTime } from 'luxon';
import { now } from '../../../../shared/utils/dateUtils.ts';
import { formatDate } from '../../../utils/fieldFormats.ts';
import { getMonthNameShort } from '../../../utils/getMonthName.ts';
import { PeriodOption } from '../report-settings/reportSettings.ts';

export interface PeriodOptionSet {
    days: PeriodOption[];
    weeks: PeriodOption[];
    months: PeriodOption[];
}

export function getPeriodOptionSet(): PeriodOptionSet {
    return {
        days: getPeriodOptions('day'),
        weeks: getPeriodOptions('week'),
        months: getPeriodOptions('month'),
    };
}

function getPeriodOptions(period: 'day' | 'week' | 'month'): PeriodOption[] {
    const dataStart = new Date('2021-10-12');

    const periodIncrements = {
        day: { days: 1 },
        week: { weeks: 1 },
        month: { months: 1 },
    };
    const plusPeriod = periodIncrements[period];

    const periods: PeriodOption[] = [];
    // Start after the fixed data limit, aligned to the next day/week/month boundary.
    let cursor = DateTime.fromJSDate(dataStart).setZone('Europe/Helsinki').plus(plusPeriod).startOf(period);
    while (true) {
        const periodEnd = cursor.plus(plusPeriod).startOf(period);
        // Include the period containing today even though its end is still in the future.
        if (cursor > DateTime.fromJSDate(now()).setZone('Europe/Helsinki')) {
            break;
        }

        periods.push(createPeriodOption(period, cursor, periodEnd));
        cursor = periodEnd;
    }
    return periods;
}

function createPeriodOption(
    period: 'day' | 'week' | 'month',
    start: DateTime,
    end: DateTime,
): PeriodOption {
    const periodName = getPeriodName(period, start);
    const graphLabels = {
        day: `${start.toFormat('dd/MM')}\n${periodName}`,
        week: periodName,
        month: `${periodName} '${start.year.toString().slice(2)}`,
    };

    return {
        // Report days follow the 06:00 boundary; calendar dates and labels stay unchanged.
        start: start.set({ hour: 6 }).toJSDate(),
        end: end.set({ hour: 6 }).toJSDate(),
        periodLabel: graphLabels[period],
        startLabel: `${periodName} - ${formatDate(start.toJSDate())}`,
        endLabel: `${periodName} - ${formatDate(end.minus({ day: 1 }).toJSDate())}`,
    };
}

function getPeriodName(period: 'day' | 'week' | 'month', start: DateTime): string {
    const periodNames = {
        day: start.toFormat('ccc', { locale: 'en' }),
        week: `Week ${start.weekNumber}`,
        month: getMonthNameShort(start.month - 1),
    };
    return periodNames[period];
}
